/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { type AxiosInstance } from 'axios';
import type { MyProfileApiV2, MyProfileDataV2 } from '../interface';
import { createQuery } from '@/utils/createReactQuery';
import { type QueryClient } from '@tanstack/react-query';
import { requestBaseURL } from '@/config/serviceAPI';
import { useAppData } from '@/hooks/useAppData';

const URL = `${requestBaseURL}/ecomm/b2c/v2/currentUser`;

type Options = object;

const getMyProfileV2 = async (options: Options, axiosInstance?: AxiosInstance) => {
  const response = await getAxios(axiosInstance).get<MyProfileApiV2>(URL, {
    headers: { Accept: 'application/json' },
  });

  return selector(response.data);
};

export const selector = (data: MyProfileApiV2): MyProfileDataV2 => data;

export const invalidateMyProfileV2Query = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: myProfileV2Query.getFullKey({}) });

const {
  useData: useMyProfileBase,
  prefetch: prefetchMyProfileV2,
  query: myProfileV2Query,
  getFromCache: getMyProfileV2FromCache,
} = createQuery<MyProfileDataV2, Options>('myProfileV2', getMyProfileV2);

type MyProfileOptions = {
  enabled?: boolean;
};

const useMyProfile = (options: MyProfileOptions = { enabled: true }) => {
  const { enabled } = options;
  const { appData } = useAppData();
  const { userAuthenticated } = appData;
  return useMyProfileBase({ enabled: userAuthenticated && enabled });
};

export {
  getMyProfileV2,
  getMyProfileV2FromCache,
  myProfileV2Query,
  prefetchMyProfileV2,
  useMyProfile,
};
